import type { WallDef } from '../types';

type Point = { x: number; y: number };
const SKIN = 0.001;

/** Conservative swept-circle collision against rotated wall rectangles.
 * Expanding both local axes by the radius also seals rectangle corner seams.
 */
export function moveInsideWalls(from: Point, target: Point, velocity: Point, radius: number, walls: WallDef[]) {
  let position = { ...from };
  let remaining = { x: target.x - from.x, y: target.y - from.y };
  const speed = { ...velocity };
  for (let pass = 0; pass < 4; pass++) {
    let first = 1;
    let normal: Point | undefined;
    for (const wall of walls) {
      const c = Math.cos(wall.angle ?? 0), s = Math.sin(wall.angle ?? 0);
      const dx = position.x - wall.x, dy = position.y - wall.y;
      const p = [dx * c + dy * s, -dx * s + dy * c];
      const d = [remaining.x * c + remaining.y * s, -remaining.x * s + remaining.y * c];
      const half = [wall.width / 2 + radius, wall.height / 2 + radius];
      let enter = -Infinity, leave = Infinity;
      let axis = 0, sign = 0;
      for (let a = 0; a < 2; a++) {
        if (Math.abs(d[a]) < 1e-12) {
          if (Math.abs(p[a]) >= half[a]) { leave = -Infinity; break; }
          continue;
        }
        const t1 = (-half[a] - p[a]) / d[a];
        const t2 = (half[a] - p[a]) / d[a];
        const near = Math.min(t1, t2);
        if (near > enter) { enter = near; axis = a; sign = -Math.sign(d[a]); }
        leave = Math.min(leave, Math.max(t1, t2));
      }
      if (enter >= -1e-9 && enter <= first && enter <= leave && leave >= 0) {
        first = Math.max(0, enter);
        normal = axis === 0 ? { x: c * sign, y: s * sign } : { x: -s * sign, y: c * sign };
      }
    }
    position.x += remaining.x * first;
    position.y += remaining.y * first;
    if (!normal) break;
    position.x += normal.x * SKIN;
    position.y += normal.y * SKIN;
    remaining = { x: remaining.x * (1 - first), y: remaining.y * (1 - first) };
    const inward = remaining.x * normal.x + remaining.y * normal.y;
    remaining.x -= Math.min(0, inward) * normal.x;
    remaining.y -= Math.min(0, inward) * normal.y;
    const vn = speed.x * normal.x + speed.y * normal.y;
    speed.x -= Math.min(0, vn) * normal.x;
    speed.y -= Math.min(0, vn) * normal.y;
  }
  return { position, velocity: speed };
}

export function clearsWalls(p: Point, radius: number, walls: WallDef[]) {
  return walls.every(w => {
    const c = Math.cos(w.angle ?? 0), s = Math.sin(w.angle ?? 0);
    const dx = p.x - w.x, dy = p.y - w.y;
    return Math.abs(dx * c + dy * s) >= w.width / 2 + radius + SKIN ||
      Math.abs(-dx * s + dy * c) >= w.height / 2 + radius + SKIN;
  });
}
